// Tray Studio main thread (spec 2026-09-27-bento-tray-design D9-D14). The DOCUMENT (src/doc.js) is
// the state: every control builds a NEW doc (documents are immutable values), layout.js lays it out
// on the main thread for the instant SVG + readouts, and the worker (geometry.worker.js via
// worker-client.js) builds the mesh 120 ms after the last change. No geometry runs here.
import '@3dp/ui/shell.css';
import './main.css';
import { publishBarHeight, publishHeaderHeight } from '@3dp/ui/bar-height';
import { pageLanguage } from '@3dp/ui/locales';
import { S } from './strings.js';
import { DEFAULT_DOC, LIMITS, BEDS, fitsBed, fmt, sanitize, clampFillet, boxDims, MAX_PRINT_Z } from './doc.js';
import { layout } from './layout.js';
import { parseId, exists, splitCell, addColumn, deleteCell, setColSize, setCellSize } from './edit.js';
import { renderSvg, labelFor } from './editor2d.js';
import { createWorkerClient } from './worker-client.js';
// preview3d.js (three.js) is loaded only via the dynamic import() in setView() below — a lazy chunk.
import { hit } from '@3dp/core/stats';
import { isLineBrowser, externalBrowserUrl, shareUrl, readShare } from './share.js';
import { wireLangSwitch } from '@3dp/ui/switch';   // ship 3 (R21): the switch's links carry the design
import { mountLangOffer } from '@3dp/ui/offer';

publishBarHeight(document.querySelector('.ui-bar'));   // --ui-bar-h: shell.css ends the desktop panel above the fixed bar
publishHeaderHeight(document.querySelector('header.ui-header')); // --ui-header-h: ship 3's taller header row; the desktop panel leaves its room too

const $ = (s) => document.querySelector(s);
const HISTORY_CAP = 100;
const PROBLEM_ORDER = ['box-narrow', 'auto-squeezed', 'too-small', 'overflow', 'fractions-over', 'corner-clipped', 'too-shallow', 'stack-wall', 'stack-shallow'];
const FRACTION_PRESETS = { '1/2': 0.5, '1/3': 0.3333, '2/3': 0.6667 };
// Union two Problem[] by code, de-duping ids under a shared code.
function mergeProblems(a, b) {
  const map = new Map();
  for (const p of [...a, ...b]) {
    const cur = map.get(p.code);
    if (cur) { for (const id of p.ids || []) if (!cur.ids.includes(id)) cur.ids.push(id); }
    else map.set(p.code, { code: p.code, ids: [...(p.ids || [])] });
  }
  return [...map.values()];
}

// A fatal worker crash is permanent for this page (the standard onFatal contract) — once true,
// renderIssues() below always shows S.fatal, so a later re-render (e.g. a plain cell tap) can
// never wipe it back out (task-6 fix 1, I1).
let fatal = false;
const worker = createWorkerClient({ url: window.__WORKER_URL__, recycleAfter: 300, onFatal: () => {
  fatal = true;
  $('#dl3mf').disabled = true; $('#dlstl').disabled = true;
  renderIssues([], false);
} });

// ---- state: doc (from a share link or the default), sel, and a { stack, i } history of both ----
const shareResult = readShare(location.hash);
let doc, linkErrorCode = null;

// ship 3 (R21): the language switch and the English-page offer. Nothing here is uploaded — the
// design whole travels in the link.
wireLangSwitch(window, { linkFor: (href) => shareUrl(href, doc), loses: () => false });
mountLangOffer(window, { linkFor: (href) => shareUrl(href, doc) });   // the offer's link carries the design too (B1)
if (shareResult === null) doc = sanitize(DEFAULT_DOC);
else if (shareResult.error) { linkErrorCode = shareResult.error.code; doc = sanitize(DEFAULT_DOC); }
else doc = shareResult.doc;
let sel = 'c0-0';   // sanitize() always leaves at least one column with at least one cell, so this always exists
let hist = { stack: [{ doc, sel }], i: 0 };
let part = 'tray', lastSizes = null, lastSizesDoc = null;
let preview = null, previewPromise = null;

// Read-only window for the browser tests and the hub's ready signal (`ready`: window.__TRAY__?.built === true).
window.__TRAY__ = { built: false, lastMs: null, problems: [], doc: () => doc, sel: () => sel, part: () => part, sceneCount: () => (preview ? preview.count() : 0), sceneRect: () => (preview ? preview.sceneRect() : null) };

hit('tray', 'open');
hit('tray', 'choice', `lang:${pageLanguage().id}`); // R25: the page's OWN address's language, never the browser's

// ---- static copy: every user-visible string comes from strings.js ----
$('#title').textContent = S.title;
$('#view').querySelector('[data-v="2d"]').textContent = S.view['2d'];
$('#view').querySelector('[data-v="3d"]').textContent = S.view['3d'];
$('#l-g-cell').textContent = S.gCell;
$('#l-g-size').textContent = S.gSize;
$('#l-g-walls').textContent = S.gWalls;
$('#l-g-stack').textContent = S.gStack;
$('#l-col-kind').textContent = S.colSize;
$('#l-cell-kind').textContent = S.cellSize;
$('#l-w').textContent = S.w;
$('#l-d').textContent = S.d;
$('#l-h').textContent = S.h;
$('#l-bed').textContent = S.bed;
$('#l-wall').textContent = S.wall;
$('#l-fillet').textContent = S.fillet;
$('#l-divider').textContent = S.divider;
$('#l-stackable').textContent = S.stackable;
$('#l-clearance').textContent = S.clearance;
$('#l-g-box').textContent = S.gBox;
$('#l-boxon').textContent = S.boxOn;
$('#l-traycount').textContent = S.trayCount;
$('#l-part').textContent = S.partLabel;
$('#l-explode').textContent = S.explode;
for (const [k, label] of Object.entries(S.parts)) $(`#part button[data-p="${k}"]`).textContent = label;
$('#cell-actions button[data-act="split"]').textContent = S.split;
$('#cell-actions button[data-act="addcol"]').textContent = S.addCol;
$('#cell-actions button[data-act="del"]').textContent = S.del;
$('#cell-actions button[data-act="undo"]').setAttribute('aria-label', S.undo);
$('#cell-actions button[data-act="redo"]').setAttribute('aria-label', S.redo);
for (const [k, label] of Object.entries(S.kinds)) {
  $(`#col-kind option[value="${k}"]`).textContent = label;
  $(`#cell-kind option[value="${k}"]`).textContent = label;
}
$('#bed').innerHTML = Object.entries(BEDS).map(([k, [w, d]]) => `<option value="${k}">${S.bedOption(w, d)}</option>`).join('');
$('#dl3mf').textContent = S.dl3mf;
$('#dlstl').textContent = S.dlstl;
$('#share').textContent = S.share;
if (linkErrorCode) {
  $('#link-notice').textContent = linkErrorCode === 'newer-version' ? S.newerLink : S.badLink;
  $('#link-notice').classList.remove('hidden');
}

// ---- pure helpers over the selected cell's size kind (auto | 1/2 | 1/3 | 2/3 | mm) ----
const clampMm = (v) => Math.round(Math.min(LIMITS.mm[1], Math.max(LIMITS.mm[0], v)) * 10) / 10;
// A fraction within 1e-4 of a preset reads as that preset; anything else is "กำหนด" shown as an extra option.
function fractionKindFor(v) {
  for (const k of Object.keys(FRACTION_PRESETS)) if (Math.abs(v - FRACTION_PRESETS[k]) < 1e-4) return k;
  return null;
}
function kindToSize(kind, mmInputValue, currentPartSize) {
  if (kind === '1/2' || kind === '1/3' || kind === '2/3') return { kind: 'fraction', value: FRACTION_PRESETS[kind] };
  if (kind === 'mm') {
    const typed = mmInputValue !== '' && mmInputValue != null && Number.isFinite(Number(mmInputValue)) ? Number(mmInputValue) : Math.round(currentPartSize * 2) / 2;
    return { kind: 'mm', value: clampMm(typed) };
  }
  return { kind: 'auto' };
}
// Syncs a kind <select> + its mm <input> to a part's size object; injects/removes the "extra" option
// for a share-linked fraction that isn't one of the three presets. Never clobbers an input the user
// is actively editing (mirrors the w/d/h D8 guard) so mid-typed decimals survive a live re-render.
function syncKindSelect(selectEl, mmEl, sizeObj) {
  let extraOpt = selectEl.querySelector('option[value="extra"]');
  if (sizeObj.kind === 'fraction' && !fractionKindFor(sizeObj.value)) {
    if (!extraOpt) { extraOpt = document.createElement('option'); extraOpt.value = 'extra'; selectEl.appendChild(extraOpt); }
    extraOpt.textContent = S.pct(fmt(sizeObj.value * 100));
  } else if (extraOpt) extraOpt.remove();
  const kindKey = sizeObj.kind === 'auto' ? 'auto' : sizeObj.kind === 'mm' ? 'mm' : (fractionKindFor(sizeObj.value) || 'extra');
  if (document.activeElement !== selectEl) selectEl.value = kindKey;
  mmEl.classList.toggle('hidden', sizeObj.kind !== 'mm');
  if (document.activeElement !== mmEl) mmEl.value = sizeObj.kind === 'mm' ? fmt(sizeObj.value) : '';
}

function renderSelCard(lay) {
  const p = parseId(sel) || { col: 0, row: 0 };
  const cell = lay.cells.find((c) => c.id === sel);
  const col = doc.grid.columns[p.col] || doc.grid.columns[0];
  const cellDoc = (col && col.cells[p.row]) || col.cells[0];
  $('#sel-name').textContent = S.selName(p.col, p.row, cell ? labelFor(cell) : '—');
  syncKindSelect($('#col-kind'), $('#col-mm'), col.size);
  syncKindSelect($('#cell-kind'), $('#cell-mm'), cellDoc.size);
}

// A line the page shows in answer to what the visitor just did is brought into view, clear of the fixed bar (unify-ui
// ruling R65; scroll-margin in main.css keeps the bar's height free): the bed warning when it first shows, a problem
// that was not on show a moment ago — after an edit, never when the page opens (`rendered`).
const shownEl = (el) => (typeof el.checkVisibility === 'function' ? el.checkVisibility() : el.offsetParent !== null);
function reveal(el) {
  if (!rendered || !shownEl(el)) return;
  const r = el.getBoundingClientRect();
  if (r.top < 0 || r.bottom > $('.bar').getBoundingClientRect().top) el.scrollIntoView({ block: 'nearest' });
}
let rendered = false, shownProblems = [];
function renderIssues(problems, failed) {
  if (fatal) { $('#issues').innerHTML = `<li>${S.fatal}</li>`; return; }   // permanent — see onFatal above
  const sorted = [...problems].sort((a, b) => PROBLEM_ORDER.indexOf(a.code) - PROBLEM_ORDER.indexOf(b.code));
  const lines = sorted.map((p) => { const s = S.problem[p.code]; return `<li>${typeof s === 'function' ? s(doc) : s || p.code}</li>`; });
  if (failed) lines.unshift(`<li>${S.buildFail}</li>`);
  $('#issues').innerHTML = lines.join('');
  const codes = [...sorted.map((p) => p.code), ...(failed ? ['build-failed'] : [])];
  if (codes.some((c) => !shownProblems.includes(c))) reveal($('#issues'));
  shownProblems = codes;
}

function syncRangeOutputs() {
  const filletMax = Math.min(doc.size.w, doc.size.d) / 4;
  $('#fillet').max = filletMax;
  $('#fillet').value = doc.params.fillet;
  $('output[for="fillet"]').value = fmt(doc.params.fillet) + S.unitMm;
  $('#wall').value = doc.params.wall;
  $('output[for="wall"]').value = fmt(doc.params.wall) + S.unitMm;
  // dividerHeight is a 0.2-1 RATIO (D12), not a millimetre length — shown as a percentage.
  $('#divider').value = doc.params.dividerHeight;
  $('output[for="divider"]').value = S.pct(Math.round(doc.params.dividerHeight * 100));
  $('#clearance').value = doc.params.clearance;
  $('output[for="clearance"]').value = fmt(doc.params.clearance) + S.unitMm;
}
// D8: the w/d/h fields keep their own typed text until blur/Enter — never overwritten mid-edit.
function syncSizeFields() {
  for (const id of ['w', 'd', 'h']) if (document.activeElement !== $('#' + id)) $('#' + id).value = fmt(doc.size[id]);
}
function syncUndoRedoButtons() {
  $('#cell-actions button[data-act="undo"]').disabled = hist.i <= 0;
  $('#cell-actions button[data-act="redo"]').disabled = hist.i >= hist.stack.length - 1;
}

// ---- render: doc → layout → SVG + every readout (main thread, instant; no worker round trip) ----
// Merges in the last non-stale build reply's problems (e.g. corner-clipped, which layout.js can
// never compute) as long as that reply was for the CURRENT doc — a selection-only render (no doc
// change) keeps them; a doc edit drops them again until the next build reply confirms the new doc
// (task-6 fix 1, I1).
function render() {
  const lay = layout(doc);
  const fromBuild = lastBuildDoc === doc && !lastBuildFailed ? lastBuildProblems : [];
  const problems = mergeProblems(lay.problems, fromBuild);
  const failedHere = lastBuildDoc === doc && lastBuildFailed;
  $('#editor').innerHTML = renderSvg(doc, lay, { sel, bad: [...new Set(problems.flatMap((p) => p.ids || []))] });
  renderSelCard(lay);
  syncSizeFields();
  $('#size').textContent = S.size(fmt(doc.size.w), fmt(doc.size.d), fmt(doc.size.h));
  $('#stack-note').textContent = S.stackNote(doc.size.h);
  const isBox = doc.mode === 'partition_box';
  const warns = [];
  // box sizes arrive only with a problem-free box build (e.g. none for a corner-clipped tray) — fall back to the tray check (F4)
  if (!isBox || !lastSizes || !lastSizes.box || lastSizesDoc !== doc) { if (!fitsBed(doc.size, doc.bed)) warns.push(S.bedWarn(...BEDS[doc.bed])); }
  else for (const k of ['tray', 'box', 'lid', 'latch']) {
    const [x, y, z] = lastSizes[k];
    if (!fitsBed({ w: x, d: y }, doc.bed)) warns.push(S.bedWarnPart(S.parts[k], fmt(x), fmt(y)));
    if (z > MAX_PRINT_Z) warns.push(S.tallWarn(S.parts[k], fmt(z)));
  }
  const warned = !$('#bed-warn').classList.contains('hidden');
  $('#bed-warn').textContent = warns.join(' · ');
  $('#bed-warn').classList.toggle('hidden', warns.length === 0);
  if (warns.length && !warned) reveal($('#bed-warn'));
  $('#boxon').checked = isBox;
  $('#box-rows').classList.toggle('hidden', !isBox);
  if (!isBox && part !== 'tray') part = 'tray';
  $('#part').querySelectorAll('button').forEach((b) => b.classList.toggle('on', b.dataset.p === part));
  if (document.activeElement !== $('#traycount')) $('#traycount').value = String(doc.box.trayCount);
  if (isBox) {
    const D = boxDims(doc);
    $('#box-note').textContent = S.boxNote(fmt(D.Wo), fmt(D.Do), fmt(D.Hb), D.N);
    $('#hw-note').textContent = S.hwNote(D.hingeXs.length);
  }
  $('#explode-row').classList.toggle('hidden', !(isBox && !$('#preview').classList.contains('hidden')));
  renderIssues(problems, failedHere);
  rendered = true;                                               // from here on a line that shows is the answer to an edit
  // M2: downloads are enabled only when BOTH the current doc's layout is valid AND the last build
  // reply was for this exact doc, succeeded, and reported no problems — never optimistically left
  // enabled between an edit that invalidates the layout and the next (120 ms debounced) build reply
  // (final review M2). A fatal worker crash (onFatal, above) disables both permanently; never
  // re-enable them here once that has happened.
  if (!fatal) {
    const buildOkForDoc = lastBuildDoc === doc && !lastBuildFailed && lastBuildProblems.length === 0;
    const downloadsDisabled = !lay.ok || !buildOkForDoc;
    $('#dl3mf').disabled = downloadsDisabled;
    $('#dlstl').disabled = downloadsDisabled;
  }
  syncRangeOutputs();
  $('#stackable').checked = doc.params.stackable;
  $('#clearance-row').classList.toggle('hidden', !doc.params.stackable);
  if (document.activeElement !== $('#bed')) $('#bed').value = doc.bed;
  syncUndoRedoButtons();
  return lay;
}

// ---- history: setLive() previews a doc with no undo step; commit() is one step; undo/redo travel ----
function hideLinkNotice() { $('#link-notice').classList.add('hidden'); }
function updateShareUrl() { window.history.replaceState(null, '', shareUrl(location.href, doc)); }
function setLive(nextDoc, nextSel = sel) {
  doc = nextDoc; sel = exists(doc, nextSel) ? nextSel : 'c0-0';
  render();
  scheduleBuild();
}
function commit(nextDoc, nextSel = sel) {
  const s = exists(nextDoc, nextSel) ? nextSel : 'c0-0';
  hist.stack = hist.stack.slice(0, hist.i + 1);
  hist.stack.push({ doc: nextDoc, sel: s });
  if (hist.stack.length > HISTORY_CAP) hist.stack.shift();
  hist.i = hist.stack.length - 1;
  doc = nextDoc; sel = s;
  hideLinkNotice();
  render();
  scheduleBuild();
  updateShareUrl();
}
function travel(entry) {
  doc = entry.doc; sel = exists(doc, entry.sel) ? entry.sel : 'c0-0';
  render();
  scheduleBuild();
  updateShareUrl();   // I5: a reload after undo/redo must restore the design actually shown, not the pre-undo one
}
function undo() { if (hist.i > 0) { hist.i--; travel(hist.stack[hist.i]); } }
function redo() { if (hist.i < hist.stack.length - 1) { hist.i++; travel(hist.stack[hist.i]); } }

// ---- worker build loop: 120 ms debounce, latest wins (stale replies from a superseded build are skipped) ----
let buildTimer = null;
function scheduleBuild() { clearTimeout(buildTimer); buildTimer = setTimeout(build, 120); }
let lastMesh = null, lastReply = null;
// lastBuildDoc/lastBuildProblems/lastBuildFailed: the most recent NON-stale reply's doc and verdict —
// render() reads these (see above) so its output survives a selection-only re-render, and so an
// ok:false reply (I2) is distinguishable from "no problems" instead of silently clearing #issues
// and re-enabling downloads.
let lastBuildDoc = null, lastBuildProblems = [], lastBuildFailed = false;
async function build() {
  $('#busy').textContent = S.updating; $('#busy').classList.remove('hidden');
  const sentDoc = doc;
  const reply = await worker.call({ type: 'build', doc: sentDoc });
  if (reply.error && reply.error.code === 'stale') return;
  $('#busy').classList.add('hidden');
  lastBuildDoc = sentDoc;
  lastBuildFailed = reply.ok === false;
  if (lastBuildFailed) {
    // A worker-level error (e.g. {code:'internal'}) carries no `problems` field at all — treat it as
    // "unknown, assume the worst", not as problems:[] (I2): downloads stay disabled and the fresh
    // layout-only problems (if any) still show, with a buildFail line ahead of them (see render()).
    lastBuildProblems = [];
    $('#dl3mf').disabled = true; $('#dlstl').disabled = true;
  } else {
    lastBuildProblems = reply.problems || [];
    lastSizes = reply.sizes || null; lastSizesDoc = sentDoc;
    window.__TRAY__.problems = lastBuildProblems;
    window.__TRAY__.lastMs = reply.ms;
    const blocked = lastBuildProblems.length > 0;
    $('#dl3mf').disabled = blocked; $('#dlstl').disabled = blocked;
    if (reply.mesh) {
      lastMesh = reply.mesh;
      lastReply = reply; if (preview) showScene();
      window.__TRAY__.built = true;
    }
  }
  render();   // re-render #issues / SVG bad classes from the merged verdict (render() itself reads lastBuild*)
  worker.countGeneration();
  worker.recycleIfDue();
}

// Box mode: the tray drawn N times at its pitch, then box, lid, latch (spec B11). Core frame, z up.
function showScene() {
  const r = lastReply; if (!r || !r.mesh) return;
  if (!r.parts) { preview.setScene([{ mesh: r.mesh, color: '#7B4FD3' }]); return; }
  const { N, pitch, trayOrigin: o } = r.meta, h = doc.size.h, lift = N * (h + 8) + 20;
  const parts = [];
  for (let i = 0; i < N; i++) parts.push({ mesh: r.mesh, color: '#7B4FD3', pos: [o[0], o[1], o[2] + i * pitch], explode: [0, 0, i * (h + 8)] });
  parts.push({ mesh: r.parts.box, color: '#5B5A7A' });
  parts.push({ mesh: r.parts.lid, color: '#8C8AA8', explode: [0, 0, lift] });
  parts.push({ mesh: r.parts.latch, color: '#E0A33A', explode: [0, -15, lift] });
  preview.setScene(parts);
}

// ---- 2D / 3D view toggle: three.js loads only when 3D is first opened (a lazy chunk) ----
async function setView(mode) {
  $('#view').querySelectorAll('button').forEach((b) => b.classList.toggle('on', b.dataset.v === mode));
  $('#editor').classList.toggle('hidden', mode !== '2d');
  $('#preview').classList.toggle('hidden', mode !== '3d');
  render();
  if (mode !== '3d') return;
  if (!preview) {
    if (!previewPromise) {
      previewPromise = import('./preview3d.js').then(({ createPreview }) => { preview = createPreview($('#preview')); });
      previewPromise.catch(() => { previewPromise = null; });
    }
    try { await previewPromise; } catch { setView('2d'); return; }
  }
  showScene();
  preview.resize();
}
$('#view').addEventListener('click', (e) => { const b = e.target.closest('button[data-v]'); if (b) setView(b.dataset.v); });

// ---- editor: tap a cell to select it (no doc change, no history step, no rebuild) ----
$('#editor').addEventListener('click', (e) => {
  const g = e.target.closest('[data-id]');
  if (!g || !exists(doc, g.dataset.id)) return;
  sel = g.dataset.id;
  render();
});

// ---- selected-cell card: kind selects (commit) + mm inputs (live, commit on change/blur) ----
function currentColWidth(colIndex) { const c = layout(doc).columns[colIndex]; return c ? c.w : 0; }
function currentCellDepth(id) { const c = layout(doc).cells.find((x) => x.id === id); return c ? c.d : 0; }
$('#col-kind').addEventListener('change', () => {
  const p = parseId(sel); if (!p) return;
  const res = setColSize(doc, sel, kindToSize($('#col-kind').value, $('#col-mm').value, currentColWidth(p.col)));
  commit(res.doc, res.sel);
});
$('#cell-kind').addEventListener('change', () => {
  const res = setCellSize(doc, sel, kindToSize($('#cell-kind').value, $('#cell-mm').value, currentCellDepth(sel)));
  commit(res.doc, res.sel);
});
$('#col-mm').addEventListener('input', () => {
  const v = Number($('#col-mm').value);
  if ($('#col-mm').value.trim() === '' || !Number.isFinite(v)) return;
  const res = setColSize(doc, sel, { kind: 'mm', value: clampMm(v) });
  setLive(res.doc, res.sel);
});
$('#col-mm').addEventListener('change', () => commit(doc, sel));
$('#cell-mm').addEventListener('input', () => {
  const v = Number($('#cell-mm').value);
  if ($('#cell-mm').value.trim() === '' || !Number.isFinite(v)) return;
  const res = setCellSize(doc, sel, { kind: 'mm', value: clampMm(v) });
  setLive(res.doc, res.sel);
});
$('#cell-mm').addEventListener('change', () => commit(doc, sel));

// ---- cell actions: split / add column / delete (one history step each) + undo/redo ----
$('#cell-actions').addEventListener('click', (e) => {
  const b = e.target.closest('button[data-act]'); if (!b) return;
  const act = b.dataset.act;
  if (act === 'undo') return undo();
  if (act === 'redo') return redo();
  const res = act === 'split' ? splitCell(doc, sel) : act === 'addcol' ? addColumn(doc, sel) : act === 'del' ? deleteCell(doc, sel) : null;
  if (res) commit(res.doc, res.sel);
});

// ---- size fields (D8): last valid value lives until blur/Enter; an invalid field never reaches doc ----
const fieldFocusValue = {};
for (const id of ['w', 'd', 'h']) {
  const el = $('#' + id), hintEl = $('#' + id + '-hint');
  el.addEventListener('focus', () => { fieldFocusValue[id] = doc.size[id]; });
  el.addEventListener('input', () => {
    const raw = el.value.trim(), n = Number(raw), [lo, hi] = LIMITS[id];
    if (raw !== '' && Number.isFinite(n) && n >= lo && n <= hi) {
      hintEl.classList.add('hidden');
      let next = { ...doc, size: { ...doc.size, [id]: n } };
      if (id === 'w' || id === 'd') next = clampFillet(next);
      setLive(next);
    } else {
      hintEl.textContent = S.numHint(lo, hi);
      hintEl.classList.remove('hidden');
    }
  });
  const commitField = () => {
    hintEl.classList.add('hidden');
    el.value = fmt(doc.size[id]);
    if (fieldFocusValue[id] !== undefined && doc.size[id] !== fieldFocusValue[id]) commit(doc, sel);
    fieldFocusValue[id] = undefined;
  };
  el.addEventListener('blur', commitField);
  el.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); el.blur(); } });
}

// ---- wall / fillet / divider / clearance ranges: live on input, one history step on change ----
function bindRange(id, paramKey) {
  const el = $('#' + id);
  el.addEventListener('input', () => setLive({ ...doc, params: { ...doc.params, [paramKey]: Number(el.value) } }));
  el.addEventListener('change', () => commit(doc, sel));
}
bindRange('wall', 'wall');
bindRange('fillet', 'fillet');
bindRange('divider', 'dividerHeight');
bindRange('clearance', 'clearance');

// ---- stackable + bed: discrete controls, commit immediately ----
$('#stackable').addEventListener('change', (e) => commit({ ...doc, params: { ...doc.params, stackable: e.target.checked } }, sel));
$('#bed').addEventListener('change', (e) => commit({ ...doc, bed: e.target.value }, sel));

// ---- box controls: on/off, part picker, explode slider, tray count ----
$('#boxon').addEventListener('change', (e) => { if (!e.target.checked) part = 'tray'; commit({ ...doc, mode: e.target.checked ? 'partition_box' : 'partition' }, sel); });
$('#part').addEventListener('click', (e) => { const b = e.target.closest('button[data-p]'); if (!b || doc.mode !== 'partition_box') return; part = b.dataset.p; render(); });
$('#explode').addEventListener('input', (e) => { if (preview) preview.setExplode(Number(e.target.value)); });
{
  const el = $('#traycount'), hintEl = $('#traycount-hint'), [lo, hi] = LIMITS.trayCount;
  el.addEventListener('input', () => {
    const raw = el.value.trim(), n = Number(raw);
    if (/^\d+$/.test(raw) && n >= lo && n <= hi) { hintEl.classList.add('hidden'); setLive({ ...doc, box: { ...doc.box, trayCount: n } }); }
    else { hintEl.textContent = S.numHint(lo, hi); hintEl.classList.remove('hidden'); }
  });
  let focusValue;
  el.addEventListener('focus', () => { focusValue = doc.box.trayCount; });
  el.addEventListener('blur', () => { hintEl.classList.add('hidden'); el.value = String(doc.box.trayCount); if (focusValue !== undefined && focusValue !== doc.box.trayCount) commit(doc, sel); focusValue = undefined; });
  el.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); el.blur(); } });
}

// ---- desktop keyboard: Ctrl/Cmd-Z undo, Shift+Ctrl/Cmd-Z redo (not while typing) ----
// M1: only TEXT-ENTRY controls should swallow the shortcut — a range slider or checkbox has focus
// after a tap/drag, not mid-typing, so undo must still fire there (final review M1).
const TEXT_ENTRY_TYPES = new Set(['text', 'number', 'search']);
function isTextEntry(el) {
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return true;
  return el instanceof HTMLInputElement && TEXT_ENTRY_TYPES.has(el.type);
}
document.addEventListener('keydown', (e) => {
  if (isTextEntry(e.target)) return;
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
    e.preventDefault();
    if (e.shiftKey) redo(); else undo();
  }
});

// ---- downloads (anchor attached before click(): a detached <a download> is ignored by Safari/iOS) ----
async function download(format) {
  const sendPart = doc.mode === 'partition_box' ? part : 'tray';
  const r = await worker.call({ type: 'export', doc, format, part: sendPart });
  worker.countGeneration(); worker.recycleIfDue();
  if (!r.ok) { $('#issues').innerHTML = `<li>${S.exportFail}</li>`; return; }
  const href = URL.createObjectURL(new Blob([r.bytes], { type: r.mime }));
  const a = document.createElement('a'); a.href = href; a.download = r.filename;
  document.body.appendChild(a); a.click(); a.remove();
  // Choices are sent once per download (D16): they measure what actually gets printed.
  hit('tray', 'download', format);
  hit('tray', 'choice', 'stack:' + (doc.params.stackable ? 'on' : 'off'));
  hit('tray', 'choice', 'divider:' + (doc.params.dividerHeight < 1 ? 'low' : 'full'));
  hit('tray', 'choice', 'bed:' + doc.bed);
  hit('tray', 'choice', 'box:' + (doc.mode === 'partition_box' ? String(doc.box.trayCount) : 'off'));
  hit('tray', 'choice', 'part:' + sendPart);
  setTimeout(() => URL.revokeObjectURL(href), 5000);
}
function wireDownload(btn, format) {
  btn.addEventListener('click', () => {
    if (isLineBrowser(navigator.userAgent)) {
      $('#line-hint').textContent = S.lineHint;
      $('#line-hint').classList.remove('hidden');
      location.href = externalBrowserUrl(location.href);
      return;
    }
    download(format);
  });
}
wireDownload($('#dl3mf'), '3mf');
wireDownload($('#dlstl'), 'stl');

// ---- share ----
$('#share').addEventListener('click', async () => {
  const u = shareUrl(location.href, doc);
  try { await navigator.clipboard.writeText(u); } catch { prompt('', u); }
  hit('tray', 'share');
  $('#share').textContent = S.copied;
  setTimeout(() => { $('#share').textContent = S.share; }, 1500);
});

// ---- start ----
render();
build();
